import { useState } from "react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, Filter, Edit, Trash2, ToggleLeft, ToggleRight, Tag, TrendingUp, Users, DollarSign } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { couponsService, type Coupon, type CouponFilters, type CreateCouponRequest } from "@/services/couponsService";

export default function Coupons() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // State management
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [filters, setFilters] = useState<CouponFilters>({
    page: 1,
    limit: 10
  });
  const [formData, setFormData] = useState<CreateCouponRequest>({
    code: '',
    type: 'percentage',
    value: 0,
    start_date: '',
    end_date: '',
    min_order_amount: 0,
    usage_limit: 0,
    status: 'active'
  });

  // Fetch coupons data
  const { data: couponsResponse, isLoading: couponsLoading, error: couponsError } = useQuery({
    queryKey: ['coupons', filters],
    queryFn: () => couponsService.getCoupons(filters),
    staleTime: 30000,
  });

  // Note: Stats endpoint removed - using calculated stats from coupons data

  const allCoupons = couponsResponse?.data?.coupons || [];
  
  // Apply client-side filtering as fallback if API doesn't handle filters
  const coupons = React.useMemo(() => {
    let filtered = [...allCoupons];
    
    // Apply search filter
    if (filters.search && filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      filtered = filtered.filter((coupon: any) => 
        coupon.code?.toLowerCase().includes(searchTerm) ||
        coupon.description?.toLowerCase().includes(searchTerm)
      );
    }
    
    // Apply type filter
    if (filters.type) {
      filtered = filtered.filter((coupon: any) => coupon.type === filters.type);
    }
    
    // Apply status filter
    if (filters.status) {
      filtered = filtered.filter((coupon: any) => coupon.status === filters.status);
    }
    
    console.log('🔧 Filtering applied:', {
      original: allCoupons.length,
      filtered: filtered.length,
      filters
    });
    
    return filtered;
  }, [allCoupons, filters]);
  
  // Add logging when data changes
  React.useEffect(() => {
    console.log('🔧 Coupons query result:', couponsResponse);
    console.log('🔧 Coupons error:', couponsError);
    console.log('🔧 All coupons:', allCoupons);
    console.log('🔧 Filtered coupons:', coupons);
    console.log('🔧 Current filters:', filters);
  }, [couponsResponse, couponsError, allCoupons, coupons, filters]);
  
  // Removed stats query logging since we're not using the stats endpoint
  
  // Calculate stats from coupons data if stats endpoint doesn't exist
  const calculatedStats = {
    total_coupons: coupons.length,
    active_coupons: coupons.filter((c: any) => c.status === 'active').length,
    expired_coupons: coupons.filter((c: any) => new Date(c.end_date) < new Date()).length,
    total_usage: coupons.reduce((sum: number, c: any) => sum + (c.used_count || 0), 0)
  };
  
  const stats = calculatedStats;

  // Create coupon mutation
  const createCouponMutation = useMutation({
    mutationFn: (data: CreateCouponRequest) => couponsService.createCoupon(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
      queryClient.invalidateQueries({ queryKey: ['coupon-stats'] });
      setIsCreateModalOpen(false);
      resetForm();
      toast({
        title: t("success"),
        description: t("couponCreatedSuccessfully") || "Coupon created successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: t("error"),
        description: error.message || "Failed to create coupon",
        variant: "destructive",
      });
    },
  });

  // Update coupon mutation
  const updateCouponMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateCouponRequest> }) => 
      couponsService.updateCoupon(id, data),
    onSuccess: (response) => {
      console.log('🔧 Update coupon successful - closing modal and resetting form');
      console.log('🔧 Update response received:', response);
      console.log('🔧 Updated coupon data:', response.data);
      
      // Force refresh the coupons list
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
      queryClient.invalidateQueries({ queryKey: ['coupon-stats'] });
      
      // Also refetch immediately to see changes
      queryClient.refetchQueries({ queryKey: ['coupons'] });
      
      setEditingCoupon(null);
      setIsCreateModalOpen(false); // Close the modal
      resetForm();
      toast({
        title: t("success"),
        description: t("couponUpdatedSuccessfully") || "Coupon updated successfully",
      });
    },
    onError: (error: any) => {
      console.error('🔧 Update coupon mutation error:', error);
      toast({
        title: t("error"),
        description: error.message || "Failed to update coupon",
        variant: "destructive",
      });
    },
  });

  // Delete coupon mutation
  const deleteCouponMutation = useMutation({
    mutationFn: (id: number) => couponsService.deleteCoupon(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
      queryClient.invalidateQueries({ queryKey: ['coupon-stats'] });
      toast({
        title: t("success"),
        description: t("couponDeletedSuccessfully") || "Coupon deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: t("error"),
        description: error.message || "Failed to delete coupon",
        variant: "destructive",
      });
    },
  });

  // Toggle coupon status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'active' | 'inactive' }) => 
      couponsService.toggleCouponStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
      queryClient.invalidateQueries({ queryKey: ['coupon-stats'] });
      toast({
        title: t("success"),
        description: t("couponStatusUpdated") || "Coupon status updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: t("error"),
        description: error.message || "Failed to update coupon status",
        variant: "destructive",
      });
    },
  });

  // Form handlers
  const resetForm = () => {
    setFormData({
      code: '',
      type: 'percentage',
      value: 0,
      start_date: '',
      end_date: '',
      min_order_amount: 0,
      usage_limit: 0,
      status: 'active'
    });
  };

  const handleCreateCoupon = () => {
    setEditingCoupon(null);
    resetForm();
    setIsCreateModalOpen(true);
  };

  const handleEditCoupon = (coupon: Coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      type: coupon.type,
      value: coupon.value,
      start_date: coupon.start_date.split('T')[0],
      end_date: coupon.end_date.split('T')[0],
      min_order_amount: coupon.min_order_amount || 0,
      usage_limit: coupon.usage_limit || 0,
      status: coupon.status
    });
    setIsCreateModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.code.trim()) {
      toast({
        title: t("error"),
        description: "Coupon code is required",
        variant: "destructive",
      });
      return;
    }

    // Check for duplicate coupon codes (only when creating or changing code)
    const isDuplicateCode = coupons.some(coupon => 
      coupon.code.toLowerCase() === formData.code.trim().toLowerCase() && 
      (!editingCoupon || coupon.id !== editingCoupon.id)
    );
    
    if (isDuplicateCode) {
      toast({
        title: t("error"),
        description: t("duplicateCouponCode") || "A coupon with this code already exists",
        variant: "destructive",
      });
      return;
    }
    
    if (formData.value <= 0) {
      toast({
        title: t("error"),
        description: "Discount value must be greater than 0",
        variant: "destructive",
      });
      return;
    }
    
    if (formData.type === 'percentage' && formData.value > 100) {
      toast({
        title: t("error"),
        description: "Percentage discount cannot be more than 100%",
        variant: "destructive",
      });
      return;
    }
    
    if (!formData.start_date || !formData.end_date) {
      toast({
        title: t("error"),
        description: "Start date and end date are required",
        variant: "destructive",
      });
      return;
    }
    
    if (new Date(formData.start_date) >= new Date(formData.end_date)) {
      toast({
        title: t("error"),
        description: "End date must be after start date",
        variant: "destructive",
      });
      return;
    }
    
    console.log('🔧 Submitting coupon form data:', formData);
    
    if (editingCoupon) {
      console.log('🔧 Updating coupon with ID:', editingCoupon.id);
      console.log('🔧 Original coupon data:', editingCoupon);
      console.log('🔧 Form data being sent:', formData);
      console.log('🔧 Comparing values:');
      console.log('  - Value: ', editingCoupon.value, ' → ', formData.value);
      console.log('  - Min Order: ', editingCoupon.min_order_amount, ' → ', formData.min_order_amount);
      console.log('  - Usage Limit: ', editingCoupon.usage_limit, ' → ', formData.usage_limit);
      console.log('  - Status: ', editingCoupon.status, ' → ', formData.status);
      updateCouponMutation.mutate({ id: editingCoupon.id, data: formData });
    } else {
      console.log('🔧 Creating new coupon');
      createCouponMutation.mutate(formData);
    }
  };

  const handleDeleteCoupon = (id: number) => {
    deleteCouponMutation.mutate(id);
  };

  const handleToggleStatus = (id: number, currentStatus: 'active' | 'inactive') => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    toggleStatusMutation.mutate({ id, status: newStatus });
  };

  const handleFilterChange = (key: keyof CouponFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("coupons")}</h1>
          <p className="text-muted-foreground">
            {t("couponsSubtitle") || "Manage discount coupons and promotional codes"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleCreateCoupon}>
            <Plus className="mr-2 h-4 w-4" />
            {t("createCoupon") || "Create Coupon"}
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={async () => {
              console.log('🔧 Testing direct API call...');
              try {
                const response = await fetch('http://localhost:3000/admin/coupons', {
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                    'Content-Type': 'application/json'
                  }
                });
                const data = await response.json();
                console.log('🔧 Direct API response:', data);
                console.log('🔧 Response status:', response.status);
              } catch (error) {
                console.error('🔧 Direct API error:', error);
              }
            }}
          >
            Test API
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("totalCoupons")}</CardTitle>
            <Tag className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_coupons}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("activeCoupons")}</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.active_coupons}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("expiredCoupons")}</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.expired_coupons}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("totalUsage")}</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_usage}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            {t("filters")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                placeholder={t("searchCoupons") || "Search by coupon code or description..."}
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="max-w-sm"
              />
            </div>
            <Select
              value={filters.type || 'all'}
              onValueChange={(value) => handleFilterChange('type', value === 'all' ? undefined : value)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t("discountType")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allTypes") || "All Types"}</SelectItem>
                <SelectItem value="percentage">{t("percentage") || "Percentage"}</SelectItem>
                <SelectItem value="fixed">{t("fixedAmount") || "Fixed Amount"}</SelectItem> 
              </SelectContent>
            </Select>
            <Select
              value={filters.status || 'all'}
              onValueChange={(value) => handleFilterChange('status', value === 'all' ? undefined : value)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t("status") || "Status"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allStatuses") || "All Statuses"}</SelectItem>
                <SelectItem value="active">{t("active") || "Active"}</SelectItem>
                <SelectItem value="inactive">{t("inactive") || "Inactive"}</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => {
                setFilters({
                  search: '',
                  type: undefined,
                  status: undefined,
                  page: 1,
                  limit: 10
                });
              }}
              className="whitespace-nowrap"
            >
              {t("clearFilters") || "Clear Filters"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Coupons Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t("couponsList") || "Coupons List"}</CardTitle>
              <CardDescription>
                {t("manageCoupons") || "Manage your discount coupons and promotional codes"}
              </CardDescription>
            </div>
            <div className="text-sm text-muted-foreground">
              {coupons.length > 0 ? (
                <>
                  {t("showing") || "Showing"} {coupons.length} {t("of") || "of"} {allCoupons.length} {t("coupons") || "coupons"}
                  {(filters.search || filters.type || filters.status) && (
                    <span className="ml-2 text-primary">({t("filtered") || "filtered"})</span>
                  )}
                </>
              ) : (
                <span>{t("noCoupons") || "No coupons found"}</span>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {couponsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-muted-foreground">{t("loading")}...</div>
            </div>
          ) : coupons.length === 0 ? (
            <div className="text-center py-8">
              <Tag className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">{t("noCoupons") || "No coupons found"}</h3>
              <p className="text-muted-foreground mb-4">
                {t("createFirstCoupon") || "Create your first coupon to get started"}
              </p>
              <Button onClick={handleCreateCoupon}>
                <Plus className="mr-2 h-4 w-4" />
                {t("createCoupon") || "Create Coupon"}
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("code")}</TableHead>
                  <TableHead>{t("discountType")}</TableHead>
                  <TableHead>{t("discountValue")}</TableHead>
                  <TableHead>{t("usage")}</TableHead>
                  <TableHead>{t("validUntil")}</TableHead>
                  <TableHead>{t("status")}</TableHead>
                  <TableHead>{t("actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {coupons.map((coupon: any) => (
                  <TableRow key={coupon.id}>
                    <TableCell className="font-medium">{coupon.code}</TableCell>
                    <TableCell>
                      <Badge variant={coupon.type === 'percentage' ? 'default' : 'secondary'}>
                        {coupon.type === 'percentage' ? '%' : '$'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {coupon.type === 'percentage' 
                        ? `${coupon.value}%` 
                        : formatCurrency(coupon.value)
                      }
                    </TableCell>
                    <TableCell>
                      {coupon.used_count} / {coupon.usage_limit || '∞'}
                    </TableCell>
                    <TableCell>{formatDate(coupon.end_date)}</TableCell>
                    <TableCell>
                      <Badge variant={coupon.status === 'active' ? 'default' : 'secondary'}>
                        {coupon.status === 'active' ? t("active") : t("inactive")}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleStatus(coupon.id, coupon.status)}
                          disabled={toggleStatusMutation.isPending}
                        >
                          {coupon.status === 'active' ? (
                            <ToggleRight className="h-4 w-4" />
                          ) : (
                            <ToggleLeft className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditCoupon(coupon)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={deleteCouponMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>{t("deleteCoupon")}</AlertDialogTitle>
                              <AlertDialogDescription>
                                {t("deleteCouponConfirmation") || "Are you sure you want to delete this coupon? This action cannot be undone."}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteCoupon(coupon.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                {t("delete")}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Coupon Dialog */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingCoupon ? t("editCoupon") : t("createCoupon")}
            </DialogTitle>
            <DialogDescription>
              {editingCoupon 
                ? (t("editCouponDescription") || "Update coupon details")
                : (t("createCouponDescription") || "Create a new discount coupon")
              }
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code">{t("couponCode")}</Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
                placeholder="SAVE20"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type">{t("discountType")}</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value: 'percentage' | 'fixed' | 'amount' | 'flat') => 
                    setFormData(prev => ({ ...prev, type: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">{t("percentage") || "Percentage"}</SelectItem>
                    <SelectItem value="fixed">{t("fixedAmount") || "Fixed Amount"}</SelectItem>
                    <SelectItem value="amount">{t("amount") || "Amount"}</SelectItem>
                    <SelectItem value="flat">{t("flat") || "Flat"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="value">{t("discountValue")}</Label>
                <Input
                  id="value"
                  type="number"
                  value={formData.value}
                  onChange={(e) => setFormData(prev => ({ ...prev, value: Number(e.target.value) }))}
                  min="0"
                  step={formData.type === 'percentage' ? '1' : '0.01'}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="min_order_amount">{t("minOrderAmount")}</Label>
                <Input
                  id="min_order_amount"
                  type="number"
                  value={formData.min_order_amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, min_order_amount: Number(e.target.value) }))}
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="usage_limit">{t("usageLimit")}</Label>
                <Input
                  id="usage_limit"
                  type="number"
                  value={formData.usage_limit}
                  onChange={(e) => setFormData(prev => ({ ...prev, usage_limit: Number(e.target.value) }))}
                  min="0"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="status">{t("status")}</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value: 'active' | 'inactive') => 
                    setFormData(prev => ({ ...prev, status: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t("active")}</SelectItem>
                    <SelectItem value="inactive">{t("inactive")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">{t("validFrom")}</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">{t("validUntil")}</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreateModalOpen(false)}
              >
                {t("cancel")}
              </Button>
              <Button
                type="submit"
                disabled={createCouponMutation.isPending || updateCouponMutation.isPending}
              >
                {editingCoupon ? t("updateCoupon") : t("createCoupon")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
